/**
 * Why a wallet sheet would not open, in one line, for a phone with no console attached.
 *
 * WHY THIS EXISTS. A sheet that fails before it appears reports the reason only to the browser console, and
 * the guests and staff who hit it are holding an iPhone. Diagnosing an Apple Pay sheet that will not open
 * meant tethering a phone to a Mac for Web Inspector — so in practice the reason was never read, and three
 * unrelated causes (an unregistered domain, a malformed request, `begin()` outside a user gesture) all
 * presented as the same blank refusal.
 *
 * Server logs cannot help here. Every one of those failures happens before the sheet contacts Apple, so
 * nothing ever reaches us: the last request the server sees is the one that started the payment.
 */

/** Opt-in, because this text is for whoever is debugging and never for a guest. See `isDiagnostic`. */
const FLAG = 'guestpay.diagnostics';

/**
 * Notices `?debug=1` at the one moment it is in the URL.
 *
 * SEPARATE FROM READING IT, and that separation is the whole point. The failure this exists to explain
 * happens on the payment step, several navigations after the guest arrived, and every one of those drops the
 * query string — so a flag read from the URL at the moment of failure is a flag that is never set. The first
 * version of this file made exactly that mistake: it looked for the parameter only while rendering the
 * failure, by which time it was long gone, and its test passed only because the test supplied the parameter
 * the app never would.
 *
 * The same problem and the same answer as `?cardOnly=true`, which `ScanRoute` writes down on arrival for the
 * identical reason. Called once at startup, so it catches the parameter on whichever load carries it.
 *
 * `?debug=0` turns it off again, because a flag with no way back off is one a shared device keeps forever.
 */
export function captureDiagnosticFlag(
    search: string = window.location.search,
    storage: Storage = localStorage,
): void {
    try {
        const asked = new URLSearchParams(search).get('debug');

        if (asked === '1') storage.setItem(FLAG, '1');
        else if (asked === '0') storage.removeItem(FLAG);
    } catch {
        // Private browsing can refuse storage entirely. A diagnostic aid must never be the thing that breaks
        // the screen it is attached to.
    }
}

/**
 * Whether to show technical detail on the failure screen.
 *
 * Storage only. Nothing here reads the URL — see `captureDiagnosticFlag` for why the parameter is never still
 * there by the time this is asked. Off unless asked for: a guest who has just failed to pay needs a sentence
 * they can act on, not an exception name.
 */
export function isDiagnostic(storage: Storage = localStorage): boolean {
    try {
        return storage.getItem(FLAG) === '1';
    } catch {
        return false;
    }
}

/**
 * An error reduced to something safe to put on a screen.
 *
 * NAME AND MESSAGE ONLY. A wallet error can carry the payment request that produced it, and a stack names
 * paths nobody reading a phone can use. `InvalidAccessError` and its sentence are the whole diagnosis for
 * every failure this is here to explain.
 */
export function describeSheetFailure(error: unknown): string | undefined {
    if (error instanceof Error) {
        const name = error.name && error.name !== 'Error' ? error.name : undefined;
        const message = error.message?.trim() || undefined;

        return [name, message].filter(Boolean).join(': ') || undefined;
    }

    if (typeof error === 'string' && error.trim()) return error.trim();

    // A thrown object of some other shape. Saying that is more use than saying nothing, and stringifying it
    // risks dragging a payment request into view.
    return error == null ? undefined : 'The wallet reported a failure with no message.';
}
